import { YInput, YInputOptions } from '../../y/form';
import { UIClassesSpec, UIRelPos, UIValue } from '../types';
import { $trigger, YBlurEvent, YEventHandlers, YFocusEvent, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { $el } from '../elements';
import { makeInputWrapper, UIAnyInputWrapper } from './input-wrapper';
import { UIDropdown, valueDropdownGenerator } from './dropdown';
import { onKey, UIKey } from '../key';
import { onAttach } from '../attach';
import { UIView, UIViewSpec } from '../view';
import { uiAnimate } from '../animate';

export type UIInputKind = 'k-frame' | 'k-line' | 'k-flat' | 'k-inline' | 'k-toolbar' | 'k-menubar' | 'k-subtle';

export const enum UIAutofill { NAME, FIRST_NAME, MIDDLE_NAME, LAST_NAME, EMAIL, ADDRESS, CITY, POSTCODE, COUNTRY, PHONE, USERNAME, PASSWORD, OLD_PASSWORD, NEW_PASSWORD, CONFIRM_PASSWORD, OTP_CODE }

export interface UIBaseInputOptions extends YInputOptions {
    title?: string;
    placeholder?: string;
    kind?: UIInputKind;
    l_deco?: UIViewSpec;
    r_deco?: UIViewSpec;
    invalid_message?: string;
    required_message?: string;
    classes?: UIClassesSpec;
}

export abstract class UIBaseInput<Value> extends UIView implements YInput<Value> {
    static default_kind: UIInputKind = 'k-frame';

    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<Value> & YBlurEvent & YFocusEvent>;

    private _title?: HTMLElement;
    private _invalid_label?: HTMLElement;
    private _invalid_mark?: boolean;
    private _disabled: boolean;
    protected invalid_message?: string;
    protected required_message?: string;
    private _required: boolean;

    protected input_elem: HTMLElement;

    okay!: boolean;
    invalid?: boolean;
    dirty: boolean;

    on_enter?: () => void;
    on_empty_backspace?: () => void;

    constructor(options: UIBaseInputOptions, input: UIViewSpec, alt_input?: UIViewSpec) {
        super('div', ['ui-input', options.kind || UIBaseInput.default_kind, 'i-input', 'i-horizontal', options.classes]);

        this.dirty = false;
        this._required = options.required || false;
        this._disabled = options.disabled || false;
        this.invalid_message = options.invalid_message;
        this.required_message = options.required_message;

        const core = $el.DIV('r-core', [input, alt_input]);
        this.ui_add(core);

        this.input_elem = input.ui_elem;
        this.input_elem.classList.add('r-field');

        if(alt_input) {
            alt_input.ui_elem.classList.add('r-field', 'r-alt');
        }

        if(options.title) {
            this.classList.add('f-title');
            this._title = $el.LABEL(options.title, 'r-title');
            if(this._required)
                this._title.appendChild($el.REQUIRED());
            core.ui_add(this._title);
        }

        if(options.l_deco) {
            this.classList.add('f-ldeco');
            const el = options.l_deco.ui_elem;
            el.classList.add('r-ldeco');
            this.ui_prepend(options.l_deco);
        }

        if(options.r_deco) {
            this.classList.add('f-rdeco');
            const el = options.r_deco.ui_elem;
            el.classList.add('r-rdeco');
            this.ui_add(options.r_deco);
        }

        if(this._disabled)
            this.classList.add('f-disabled');
    }

    protected fixDesign() {
        if(this.is_empty) {
            this.classList.add('f-empty');
            this.classList.remove('f-notempty');
        } else {
            this.classList.remove('f-empty');
            this.classList.add('f-notempty');
        }
    }

    protected setOkay(o: boolean) {
        if(o === this.okay) return;

        if(o && this._invalid_mark) {
            this.classList.remove('f-invalid');
            this._invalid_mark = false;
        }

        this.okay = o;
        $trigger(this, 'state');
    }

    protected setDirty(d: boolean) {
        if(this._invalid_mark) {
            this.classList.remove('f-invalid');
            this._invalid_mark = false;
        }

        if(d === this.dirty) return;

        this.dirty = d;
        $trigger(this, 'state');
    }

    protected doSetDisabled(_d: boolean) {
    }

    protected abstract doCheck(): void;

    check() {
        this.doCheck();
    }

    markInvalid(focus: boolean, force?: boolean) {
        if(this.okay && !force) return false;
        if(focus)
            this.focus();
        uiAnimate([{ what: this, animation: 'flash-invalid', duration: 1.5 }]);
        if(!this._invalid_mark) {
            this.showError(this.invalid ? this.invalid_message : this.required_message);
        }
        return focus;
    }

    showError(error?: string) {
        this._invalid_mark = true;
        this.classList.add('f-invalid');
        if(error) {
            if(!this._invalid_label) {
                this.ui_add(this._invalid_label = $el.DIV('r-invalid-message', error));
            } else {
                this._invalid_label.textContent = error;
            }
        }
    }

    focus() {
        this.input_elem.focus();
    }

    blur() {
        this.input_elem.blur();
    }

    get disabled() {
        return this._disabled;
    }

    set disabled(d: boolean) {
        this._disabled = d;
        this.classList.toggle('f-disabled', d);
        this.doSetDisabled(d)
    }

    get required() {
        return this._required;
    }

    set required(r: boolean) {
        this._required = r;
        this.doCheck();
    }

    abstract get is_empty(): boolean;
    abstract get value(): Value;
    abstract set value(v: Value);
}

export type UITextInputValidator = RegExp | ((text: string, value: any) => boolean)
export type UITextInputFixer = (text: string, pos: number) => ([string, number | undefined] | undefined);

export interface UITextBasedInputOptions<Value> extends UIBaseInputOptions {
    autoresize?: boolean;
    resizeable?: boolean;
    focus_select?: boolean;
    autofill?: UIAutofill | string;
    lines?: number;
    chars?: number;
    sensitive?: boolean;
    autocapitalize?: boolean;
    blur_on_enter?: boolean;
    tab_focus?: boolean;
    autocomplete?: (term: string) => Promise<UIValue<Value>[]>;
    autofocus?: boolean;
    autofocus_select?: boolean;
    fixer?: UITextInputFixer;
}

export abstract class UITextBasedInput<T> extends UIBaseInput<T> {
    private _text: string;
    private _wrapper: UIAnyInputWrapper;

    private _autocomplete?: (term: string) => Promise<UIValue<T>[]>;
    private _autocomplete_token?: {};
    private _autocomplete_popup?: UIDropdown;
    private _fixer?: UITextInputFixer

    on_autocomplete?: (v: UIValue<T>) => void;

    constructor(options: UITextBasedInputOptions<T>) {
        const wrapper = makeInputWrapper(options);
        const input = wrapper.element;

        super(options, input);
        this._wrapper = wrapper;

        this._autocomplete = options.autocomplete;
        this._fixer = options.fixer;
        this._text = '';

        onKey(input, e => {
            if(e.key === UIKey.Backspace) {
                if(this.on_empty_backspace && !wrapper.text) {
                    this.on_empty_backspace();
                    return true;
                }
            } else if(e.key === UIKey.Enter) {
                if(this.on_enter) {
                    this.on_enter();
                    return true;
                }
            }
            return;
        });

        if(options.tab_focus === false)
            input.tabIndex = -1;

        if((options.lines && options.lines > 1) || options.resizeable) {
            this.classList.add('m-sized');
            if(options.resizeable)
                this.classList.add('m-resizeable');
            this.root.style.height = (1.6 * (2 + (options.lines || 1))) + 'em';
        }

        input.addEventListener('input', () => { this.onInput(true, true) });

        input.addEventListener('blur', () => {
            this.classList.remove('f-focus');
            this._checkAutofillChange(true);
            this.onBlur && this.onBlur();
            $trigger(this, 'blur');
        });

        input.addEventListener('focus', () => {
            this.classList.add('f-focus');
            this._checkAutofillChange(true);
            this.onFocus && this.onFocus();
            $trigger(this, 'focus');
        });

        this.doCheck();

        if(options.autofocus) {
            onAttach(this, () => {
                if(options.autofocus_select)
                    this.selectAll();
                this.focus();
            });
        }
    }

    onInput(notify: boolean, manual: boolean) {
        this.setDirty(true);

        const wrapper = this._wrapper;
        const original = wrapper.text;
        let text = original;

        if(this._fixer) {
            const fix = this._fixer(text, wrapper.selectionStart);
            if(fix) {
                text = fix[0];
                wrapper.text = text;
                if(fix[1] != undefined)
                    wrapper.select(fix[1]);
            }
        }

        if(manual && this._autocomplete && text !== this._text) {
            if(this._autocomplete_popup) {
                this._autocomplete_popup.close();
                this._autocomplete_popup = undefined;
            }

            if(text) {
                const autocomplete = this._autocomplete;
                const token = this._autocomplete_token = {};
                setTimeout(() => {
                    if(token != this._autocomplete_token) return;
                    autocomplete(text).then(options => {
                        if(token != this._autocomplete_token) return;

                        const popup = new UIDropdown(options, valueDropdownGenerator)
                        popup.on_choose = v => {
                            this.value = v.key;
                            this.on_autocomplete && this.on_autocomplete(v);
                        };
                        this._autocomplete_popup && this._autocomplete_popup.close();
                        this._autocomplete_popup = popup;
                        popup.open(this, { position: UIRelPos.Below | UIRelPos.End, adjust_size: true, match_size: true, capture_mouse: true, can_flip: true });
                    });
                }, 500);
            }
        }

        this._onText(text, notify);
    }

    private _checkAutofillChange(notify: boolean) {
        //We only do this because of autofill not firing the right events sometimes
        if(this._wrapper.text != this._text)
            this.onInput(notify, false);
    }

    private _onText(text: string, notify: boolean) {
        this._text = text;
        this.onText(text, notify);
        this.fixDesign();
        this.doCheck();
    }

    selectAll() {
        this._wrapper.select();
    }

    protected override doSetDisabled(d: boolean) {
        this._wrapper.disabled = d;
    }

    protected setText(text: string, notify: boolean) {
        this._wrapper.text = text;
        this._onText(text, notify);
    }

    get is_empty() {
        return !this._text;
    }

    protected abstract onText(text: string, notify: boolean): void;
    protected onBlur?(): void;
    protected onFocus?(): void;
}
